import React, { useState, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import logoWhite from '../assets/logo_white_transparent.png';

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── Nav bar ── */
const Nav = styled.nav`
  position: fixed; top: 0; width: 100%; z-index: 500;
  padding: 0 64px; height: 76px;
  display: flex; align-items: center; justify-content: space-between;
  transition: all 0.4s ease;
  overflow: hidden;
  ${({ $solid }) => $solid ? css`
    background: rgba(2,2,14,0.97);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(0,163,255,0.08);
  ` : css`
    background: linear-gradient(to bottom, rgba(2,2,14,0.75) 0%, transparent 100%);
  `}
  @media (max-width: 1024px) { padding: 0 32px; }
  @media (max-width: 600px)  { padding: 0 20px; height: 64px; }
`;

/* ── Logo ── */
const LogoLink = styled(Link)`
  display: flex; align-items: center; text-decoration: none; flex-shrink: 0; z-index: 2;
`;
const LogoImg = styled.img`
  height: 52px; width: auto; object-fit: contain; display: block;
  @media (max-width: 600px) { height: 40px; }
`;

/* ── Desktop links ── */
const Links = styled.ul`
  display: flex; gap: 36px; list-style: none; align-items: center;
  @media (max-width: 860px) { display: none; }
`;
const NavLink = styled(Link)`
  text-decoration: none;
  color: ${({ $active }) => $active ? '#00A3FF' : 'rgba(255,255,255,0.75)'};
  font-family: 'Rajdhani', sans-serif; font-weight: 600;
  font-size: 12px; letter-spacing: 0.22em; text-transform: uppercase;
  transition: color 0.3s; position: relative;
  &::after {
    content: ''; position: absolute; bottom: -5px; left: 0;
    width: ${({ $active }) => $active ? '100%' : '0'}; height: 1px;
    background: #00A3FF; transition: width 0.3s;
  }
  &:hover { color: #00A3FF; &::after { width: 100%; } }
`;
const NavAnchor = styled.a`
  text-decoration: none; color: rgba(255,255,255,0.75);
  font-family: 'Rajdhani', sans-serif; font-weight: 600;
  font-size: 12px; letter-spacing: 0.22em; text-transform: uppercase;
  transition: color 0.3s; position: relative;
  &::after { content: ''; position: absolute; bottom: -5px; left: 0; width: 0; height: 1px; background: #00A3FF; transition: width 0.3s; }
  &:hover { color: #00A3FF; &::after { width: 100%; } }
`;
const PlayLink = styled(Link)`
  display: inline-flex; align-items: center; gap: 7px;
  font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 12px;
  letter-spacing: 0.2em; text-transform: uppercase; color: #00FF88; text-decoration: none;
  transition: all 0.3s;
  &::before { content: '▶'; font-size: 10px; }
  &:hover { color: #fff; text-shadow: 0 0 10px #00FF88; }
`;
const GueblyBadge = styled.a`
  display: flex; align-items: center; gap: 8px;
  border: 1px solid rgba(0,163,255,0.2); padding: 8px 18px;
  text-decoration: none; font-family: 'Space Mono', monospace;
  font-size: 10px; letter-spacing: 0.14em; color: rgba(255,255,255,0.7); text-transform: uppercase;
  clip-path: polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%);
  transition: all 0.3s; flex-shrink: 0;
  span { color: #00A3FF; font-weight: 700; }
  &:hover { border-color: rgba(0,163,255,0.45); color: rgba(255,255,255,0.8); background: rgba(0,163,255,0.05); }
  @media (max-width: 860px) { display: none; }
`;

/* ── Language toggle ── */
const LangToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 1px solid rgba(0,163,255,0.2);
  padding: 5px 10px;
  cursor: pointer;
  font-family: 'Space Mono', monospace;
  font-size: 9px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.7);
  transition: all 0.3s;
  flex-shrink: 0;
  z-index: 2;

  &:hover {
    border-color: rgba(0,163,255,0.5);
    color: rgba(255,255,255,0.9);
  }

  .lang-opt {
    transition: color 0.2s;
  }
  .lang-opt.active {
    color: #00A3FF;
    font-weight: 700;
  }
  .lang-sep {
    color: rgba(255,255,255,0.3);
    margin: 0 1px;
  }
`;

/* ── Hamburger button ── */
const HamBtn = styled.button`
  display: none;
  flex-direction: column; justify-content: center; align-items: center;
  gap: 5px; width: 40px; height: 40px;
  background: transparent; border: none; cursor: pointer; z-index: 2;
  @media (max-width: 860px) { display: flex; }
`;
const HamLine = styled.span`
  display: block; width: 24px; height: 2px;
  background: #fff; border-radius: 2px;
  transition: all 0.3s;
  &:nth-child(1) { transform: ${({ $open }) => $open ? 'translateY(7px) rotate(45deg)' : 'none'}; }
  &:nth-child(2) { opacity: ${({ $open }) => $open ? 0 : 1}; transform: ${({ $open }) => $open ? 'scaleX(0)' : 'none'}; }
  &:nth-child(3) { transform: ${({ $open }) => $open ? 'translateY(-7px) rotate(-45deg)' : 'none'}; }
`;

/* ── Mobile drawer ── */
const Drawer = styled.div`
  display: none;
  @media (max-width: 860px) {
    display: ${({ $open }) => $open ? 'flex' : 'none'};
    flex-direction: column;
    position: fixed; top: 64px; left: 0; right: 0; width: 100%; max-width: 100vw; overflow-x: hidden;
    background: rgba(2,2,14,0.98); backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(0,163,255,0.12);
    padding: 24px 24px 32px; gap: 4px; z-index: 499;
    animation: ${slideDown} 0.25s ease forwards;
  }
  @media (min-width: 601px) {
    top: 76px;
  }
`;
const DrawerLink = styled(Link)`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px;
  font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 16px;
  letter-spacing: 0.18em; text-transform: uppercase; text-decoration: none;
  color: ${({ $active }) => $active ? '#00A3FF' : 'rgba(255,255,255,0.82)'};
  border-bottom: 1px solid rgba(255,255,255,0.05); transition: color 0.2s;
  &:hover { color: #00A3FF; }
  span { font-size: 12px; opacity: 0.65; }
`;
const DrawerAnchor = styled.a`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px;
  font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 16px;
  letter-spacing: 0.18em; text-transform: uppercase; text-decoration: none;
  color: rgba(255,255,255,0.82);
  border-bottom: 1px solid rgba(255,255,255,0.05); transition: color 0.2s;
  &:hover { color: #00A3FF; }
  span { font-size: 12px; opacity: 0.65; }
`;
const DrawerPlay = styled(Link)`
  display: flex; align-items: center; gap: 10px;
  padding: 16px; margin-top: 12px;
  font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 15px;
  letter-spacing: 0.2em; text-transform: uppercase; text-decoration: none;
  color: #fff; background: linear-gradient(135deg, #00A3FF, #6B00FF);
  clip-path: polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%);
  justify-content: center; transition: opacity 0.2s;
  &:hover { opacity: 0.88; }
  &::before { content: '▶'; font-size: 11px; }
`;
const DrawerBadge = styled.a`
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 12px; margin-top: 6px;
  font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.14em;
  color: rgba(255,255,255,0.65); text-decoration: none; text-transform: uppercase;
  border: 1px solid rgba(0,163,255,0.15);
  span { color: #00A3FF; font-weight: 700; }
`;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n: i18nHook } = useTranslation();
  const currentLang = i18nHook.language === 'en' ? 'en' : 'pt';

  const isHome  = location.pathname === '/';
  const isGames = location.pathname === '/games' || location.pathname.startsWith('/play/') || location.pathname.startsWith('/games/');
  const isNews  = location.pathname === '/news' || location.pathname.startsWith('/news/');

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    fn();
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Close drawer on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const close = () => setMenuOpen(false);

  const toggleLang = () => {
    const next = currentLang === 'pt' ? 'en' : 'pt';
    i18n.changeLanguage(next);
    localStorage.setItem('lang', next);
  };

  return (
    <>
      <Nav $solid={scrolled || !isHome}>
        <LogoLink to="/">
          <LogoImg src={logoWhite} alt="Guebly Games" />
        </LogoLink>

        {/* Desktop */}
        <Links>
          {isHome ? <>
            <li><NavAnchor href="#manifesto">{t('navbar.manifesto')}</NavAnchor></li>
            <li><NavAnchor href="#pilares">{t('navbar.pillars')}</NavAnchor></li>
            <li><NavAnchor href="#frentes">{t('navbar.fronts')}</NavAnchor></li>
            <li><NavLink to="/games" $active={isGames}>{t('navbar.games')}</NavLink></li>
            <li><NavLink to="/news"  $active={isNews}>{t('navbar.news')}</NavLink></li>
            <li><PlayLink to="/games">{t('navbar.play')}</PlayLink></li>
          </> : <>
            <li><NavLink to="/">{t('navbar.home')}</NavLink></li>
            <li><NavLink to="/games" $active={isGames}>{t('navbar.games')}</NavLink></li>
            <li><NavLink to="/news"  $active={isNews}>{t('navbar.news')}</NavLink></li>
            <li><PlayLink to="/games">{t('navbar.play')}</PlayLink></li>
          </>}
        </Links>

        <GueblyBadge href="https://www.guebly.com.br" target="_blank" rel="noopener noreferrer">
          {t('navbar.partOf')} <span>Guebly</span>
        </GueblyBadge>

        <LangToggle onClick={toggleLang} aria-label="Toggle language">
          <span className={`lang-opt${currentLang === 'pt' ? ' active' : ''}`}>PT</span>
          <span className="lang-sep">|</span>
          <span className={`lang-opt${currentLang === 'en' ? ' active' : ''}`}>EN</span>
        </LangToggle>

        {/* Hamburger */}
        <HamBtn onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
          <HamLine $open={menuOpen} />
          <HamLine $open={menuOpen} />
          <HamLine $open={menuOpen} />
        </HamBtn>
      </Nav>

      {/* Mobile drawer */}
      <Drawer $open={menuOpen}>
        {isHome ? <>
          <DrawerAnchor href="#manifesto" onClick={close}>{t('navbar.manifesto')} <span>→</span></DrawerAnchor>
          <DrawerAnchor href="#pilares"   onClick={close}>{t('navbar.pillars')} <span>→</span></DrawerAnchor>
          <DrawerAnchor href="#frentes"   onClick={close}>{t('navbar.fronts')} <span>→</span></DrawerAnchor>
          <DrawerLink to="/games" $active={isGames} onClick={close}>{t('navbar.games')} <span>→</span></DrawerLink>
          <DrawerLink to="/news"  $active={isNews}  onClick={close}>{t('navbar.news')} <span>→</span></DrawerLink>
        </> : <>
          <DrawerLink to="/"      onClick={close}>{t('navbar.home')} <span>→</span></DrawerLink>
          <DrawerLink to="/games" $active={isGames} onClick={close}>{t('navbar.games')} <span>→</span></DrawerLink>
          <DrawerLink to="/news"  $active={isNews}  onClick={close}>{t('navbar.news')} <span>→</span></DrawerLink>
        </>}
        <DrawerPlay to="/games" onClick={close}>{t('navbar.playNow')}</DrawerPlay>
        <DrawerBadge href="https://www.guebly.com.br" target="_blank" rel="noopener noreferrer">
          {t('navbar.partOf')} <span>Guebly</span>
        </DrawerBadge>
      </Drawer>
    </>
  );
}
